import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus,
  Eye,
  Edit,
  Download,
  Share,
  TrendingUp,
  Leaf,
  Shield,
  Calendar,
  DollarSign,
  FileText,
  Award,
  Filter,
  Search
} from 'lucide-react';
import api from '../../utils/api';

const MyProjects = () => {
  const [activeTab, setActiveTab] = useState('certificates');
  const [certificates, setCertificates] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchMyProjects();
    fetchCertificates();
  }, []);

  const fetchMyProjects = async () => {
    setLoading(true);
    try {
      const response = await api.projects.getMyProjects();
      setMyProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching my projects:', error);
      setMyProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await api.transactions.getMyCertificates();
      setCertificates(response.data || []);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      setCertificates([]);
    }
  };

  // Mock data
  const mockCertificates = [
    {
      id: 'CC-1234567890-ABC123',
      projectTitle: 'Amazon Rainforest Conservation',
      projectId: 1,
      creditsOwned: 50,
      totalValue: 1275.00,
      purchaseDate: '2024-01-15',
      status: 'retired',
      retirementReason: 'Carbon Neutral Operations',
      verificationStandard: 'Gold Standard',
      co2Offset: 50
    },
    {
      id: 'CC-2345678901-DEF456',
      projectTitle: 'Solar Farm Maharashtra',
      projectId: 2,
      creditsOwned: 25,
      totalValue: 468.50,
      purchaseDate: '2024-01-10',
      status: 'active',
      retirementReason: null,
      verificationStandard: 'VCS',
      co2Offset: 25
    },
    {
      id: 'CC-3456789012-GHI789',
      projectTitle: 'Wind Energy Project Kenya',
      projectId: 3,
      creditsOwned: 75,
      totalValue: 1711.50,
      purchaseDate: '2024-01-05',
      status: 'retired',
      retirementReason: 'CSR Initiative',
      verificationStandard: 'CDM',
      co2Offset: 75
    }
  ];

  const mockProjects = [
    {
      id: 1,
      title: 'Reforestation Colombia',
      status: 'verified',
      totalCredits: 40000,
      creditsSold: 12000,
      pricePerCredit: 20,
      revenue: 240000,
      verificationStatus: 'Gold Standard',
      createdDate: '2023-06-15',
      completionDate: '2026-03-31',
      location: 'Colombia'
    },
    {
      id: 2,
      title: 'Mangrove Restoration Thailand',
      status: 'pending-verification',
      totalCredits: 25000,
      creditsSold: 0,
      pricePerCredit: 30,
      revenue: 0,
      verificationStatus: 'Under Review',
      createdDate: '2023-12-01',
      completionDate: '2025-08-15',
      location: 'Thailand'
    }
  ];

  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = cert.projectTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         cert.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || cert.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const filteredProjects = myProjects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const totalCO2Offset = certificates.reduce((sum, cert) => sum + cert.co2Offset, 0);
  const totalInvestment = certificates.reduce((sum, cert) => sum + cert.totalValue, 0);
  const totalRevenue = myProjects.reduce((sum, project) => sum + project.revenue, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Dashboard</h1>
            <p className="text-gray-600">Manage your carbon credit portfolio and projects</p>
          </div>
          <Link
            to="/marketplace/create"
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Project
          </Link>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total CO2 Offset</p>
                <p className="text-2xl font-bold text-green-600">{totalCO2Offset}</p>
                <p className="text-xs text-gray-500">tons CO2e</p>
              </div>
              <Leaf className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Certificates Owned</p>
                <p className="text-2xl font-bold text-blue-600">{certificates.length}</p>
                <p className="text-xs text-gray-500">active certificates</p>
              </div>
              <Award className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Investment</p>
                <p className="text-2xl font-bold text-purple-600">${totalInvestment.toLocaleString()}</p>
                <p className="text-xs text-gray-500">in carbon credits</p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Project Revenue</p>
                <p className="text-2xl font-bold text-yellow-600">${totalRevenue.toLocaleString()}</p>
                <p className="text-xs text-gray-500">from sales</p>
              </div>
              <TrendingUp className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('certificates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'certificates'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            My Certificates ({certificates.length})
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'projects'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            My Projects ({myProjects.length})
          </button>
        </nav>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            {activeTab === 'certificates' ? (
              <>
                <option value="active">Active</option>
                <option value="retired">Retired</option>
              </>
            ) : (
              <>
                <option value="verified">Verified</option>
                <option value="pending-verification">Pending Verification</option>
                <option value="draft">Draft</option>
              </>
            )}
          </select>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'certificates' ? (
        <div className="space-y-6">
          {filteredCertificates.length > 0 ? (
            filteredCertificates.map((certificate) => (
              <div key={certificate.id} className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 mr-3">
                        {certificate.projectTitle}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        certificate.status === 'retired' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {certificate.status === 'retired' ? 'Retired' : 'Active'}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                        <Shield className="h-3 w-3 mr-1" />
                        {certificate.verificationStandard}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Certificate ID: {certificate.id}</p>
                    {certificate.retirementReason && (
                      <p className="text-sm text-gray-600">Retirement Reason: {certificate.retirementReason}</p>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/marketplace/project/${certificate.projectId}`}
                      className="text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100"
                      title="View Project"
                    >
                      <Eye className="h-5 w-5" />
                    </Link>
                    <button
                      className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-100"
                      title="Download Certificate"
                    >
                      <Download className="h-5 w-5" />
                    </button>
                    <button
                      className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-100"
                      title="Share Certificate"
                    >
                      <Share className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Credits Owned</p>
                    <p className="text-lg font-semibold text-gray-900">{certificate.creditsOwned}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">CO2 Offset</p>
                    <p className="text-lg font-semibold text-gray-900">{certificate.co2Offset} tons</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Value</p>
                    <p className="text-lg font-semibold text-gray-900">${certificate.totalValue.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Purchase Date</p>
                    <p className="text-lg font-semibold text-gray-900">{new Date(certificate.purchaseDate).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No certificates found</h3>
              <p className="text-gray-600 mb-4">Start offsetting your carbon footprint by purchasing credits</p>
              <Link
                to="/marketplace"
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg"
              >
                Browse Projects
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <div key={project.id} className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 mr-3">
                        {project.title}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        project.status === 'verified' 
                          ? 'bg-green-100 text-green-800'
                          : project.status === 'pending-verification'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status === 'verified' 
                          ? 'Verified' 
                          : project.status === 'pending-verification'
                          ? 'Pending Verification'
                          : 'Draft'}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                        <Shield className="h-3 w-3 mr-1" />
                        {project.verificationStatus}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Location: {project.location} • Created: {new Date(project.createdDate).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      Completion: {new Date(project.completionDate).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/marketplace/project/${project.id}`}
                      className="text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100"
                      title="View Project"
                    >
                      <Eye className="h-5 w-5" />
                    </Link>
                    <button
                      className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-100"
                      title="Edit Project"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-100"
                      title="Download Report"
                    >
                      <FileText className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Credits</p>
                    <p className="text-lg font-semibold text-gray-900">{project.totalCredits.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Credits Sold</p>
                    <p className="text-lg font-semibold text-gray-900">{project.creditsSold.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Price per Credit</p>
                    <p className="text-lg font-semibold text-gray-900">${project.pricePerCredit}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Revenue</p>
                    <p className="text-lg font-semibold text-gray-900">${project.revenue.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Progress</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {Math.round((project.creditsSold / project.totalCredits) * 100)}%
                    </p>
                  </div>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${(project.creditsSold / project.totalCredits) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
              <p className="text-gray-600 mb-4">Create your first carbon credit project to get started</p>
              <Link
                to="/marketplace/create"
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Project
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyProjects; 